import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { AlertTriangle, CheckCircle, Info, ArrowRight } from 'lucide-react';
import { InsightData } from '@/data/mockData';

interface InsightCardProps {
  data: InsightData;
  onActionClick?: (insight: InsightData) => void;
}

const iconMap = {
  success: CheckCircle,
  warning: AlertTriangle,
  info: Info
};

const colorMap = {
  success: {
    icon: 'text-green-600',
    badge: 'bg-green-100 text-green-800 hover:bg-green-100',
    border: 'border-l-green-500'
  },
  warning: {
    icon: 'text-orange-600',
    badge: 'bg-orange-100 text-orange-800 hover:bg-orange-100',
    border: 'border-l-orange-500'
  },
  info: {
    icon: 'text-blue-600',
    badge: 'bg-blue-100 text-blue-800 hover:bg-blue-100',
    border: 'border-l-blue-500'
  }
};

export const InsightCard: React.FC<InsightCardProps> = ({ data, onActionClick }) => {
  const Icon = iconMap[data.type];
  const colors = colorMap[data.type];
  
  return (
    <Card className={`transition-smooth hover:shadow-elegant animate-fade-in-up border-l-4 ${colors.border}`}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex items-center space-x-2">
            <Icon className={`h-5 w-5 ${colors.icon}`} />
            <CardTitle className="text-base font-semibold">{data.title}</CardTitle>
          </div>
          <Badge 
            variant="secondary"
            className={`text-xs ${colors.badge}`}
          >
            {data.priority}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <CardDescription className="text-sm mb-4">
          {data.description}
        </CardDescription>
        {data.action && (
          <Button 
            variant="outline" 
            size="sm"
            className="w-full justify-between transition-smooth hover:shadow-md"
            onClick={() => onActionClick?.(data)}
          >
            {data.action}
            <ArrowRight className="h-4 w-4" />
          </Button>
        )}
      </CardContent>
    </Card>
  );
};